import React, { useState, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { normalizeBackendApiBase } from '../utils/backendBaseUrl';
import { TERMS_VERSION, termsUrl } from '../config/termsVersion';
import './UserAuth.css';
import PasswordReset from './PasswordReset';

const UserAuth = ({ onLogin, backendUrl, onBack, registerOnly = false }) => {
  const { t, i18n } = useTranslation();
  const [isLogin, setIsLogin] = useState(!registerOnly);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // emailCheck.status: 'idle' | 'checking' | 'ok' | 'invalid_format' | 'unreachable_domain' | 'rate_limited'
  const [emailCheck, setEmailCheck] = useState({ status: 'idle' });
  const emailCheckSeqRef = useRef(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const currentTermsLocale = (i18n.language || 'de').toLowerCase().startsWith('en') ? 'en' : 'de';
  const currentTermsUrl = termsUrl(currentTermsLocale);

  const rawBackend =
    backendUrl ||
    (typeof window !== 'undefined' ? localStorage.getItem('stoppuhr_backend') : null) ||
    (typeof window !== 'undefined' ? localStorage.getItem('backendUrl') : null) ||
    'http://localhost:8081';
  const normalizedBackendUrl = normalizeBackendApiBase(rawBackend) || 'http://localhost:8081';

  const runEmailCheck = async (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      setEmailCheck({ status: 'idle' });
      return;
    }
    const seq = ++emailCheckSeqRef.current;
    setEmailCheck({ status: 'checking' });
    try {
      const response = await fetch(`${normalizedBackendUrl}/api/user/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      if (seq !== emailCheckSeqRef.current) return;
      if (response.status === 429) {
        setEmailCheck({ status: 'rate_limited' });
        return;
      }
      if (!response.ok) {
        setEmailCheck({ status: 'idle' });
        return;
      }
      const data = await response.json();
      if (seq !== emailCheckSeqRef.current) return;
      if (!data.format_valid) {
        setEmailCheck({ status: 'invalid_format' });
      } else if (!data.domain_resolvable) {
        setEmailCheck({ status: 'unreachable_domain' });
      } else {
        setEmailCheck({ status: 'ok' });
      }
    } catch {
      if (seq === emailCheckSeqRef.current) {
        setEmailCheck({ status: 'idle' });
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${normalizedBackendUrl}/api/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const msg =
          data.error ||
          data.message ||
          t('user_auth.errors.login_failed_http', { status: response.status });
        throw new Error(msg);
      }

      // Speichere Token und User-Info
      localStorage.setItem('userToken', data.token);
      localStorage.setItem('userInfo', JSON.stringify(data.user));
      
      if (onLogin) {
        onLogin(data.user, data.token);
      }
    } catch (err) {
      setError(err.message || t('user_auth.errors.login_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validierung
    if (!email.trim() || !email.includes('@') || !email.includes('.')) {
      setError(t('user_auth.errors.email_invalid_simple'));
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError(t('user_auth.errors.password_min_length'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError(t('user_auth.errors.passwords_mismatch'));
      setLoading(false);
      return;
    }

    if (!name.trim()) {
      setError(t('user_auth.errors.name_required'));
      setLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setError(t('user_auth.errors.terms_required'));
      setLoading(false);
      return;
    }

    // Email-Format Validierung
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('user_auth.errors.email_invalid_format'));
      setLoading(false);
      return;
    }

    // Live-Check Ergebnis blockt nur bei 'invalid_format' (unreachable_domain ist
    // nur eine Warnung, da MX-Lookups false-positiv sein koennen).
    if (emailCheck.status === 'invalid_format') {
      setError(t('user_auth.errors.email_invalid_format'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${normalizedBackendUrl}/api/user/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          name: name.trim(),
          company: company.trim() || null,
          address: address.trim() || null,
          phone: phone.trim() || null,
          accepted_terms: true,
          terms_version: TERMS_VERSION,
          terms_locale: currentTermsLocale,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || t('user_auth.errors.register_failed'));
      }

      setSuccess(data.message || t('user_auth.info.register_success'));
      
      // Nach erfolgreicher Registrierung: im normalen Flow auf Login wechseln,
      // im registerOnly-Flow zurück zur App-Anmeldung gehen.
      setTimeout(() => {
        if (registerOnly) {
          if (onBack) onBack();
          return;
        }
        setIsLogin(true);
        setPassword('');
        setConfirmPassword('');
        setAcceptedTerms(false);
        setEmailCheck({ status: 'idle' });
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message || t('user_auth.errors.register_failed'));
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <PasswordReset
        backendUrl={normalizedBackendUrl}
        initialToken=""
        onCancel={() => setShowForgotPassword(false)}
        onSuccess={() => {
          setShowForgotPassword(false);
          setIsLogin(true);
          setPassword('');
          setSuccess(t('user_auth.info.password_changed_now_login'));
        }}
      />
    );
  }

  return (
    <div className="user-auth-container">
      <div className="user-auth-card">
        {onBack && (
          <button 
            onClick={onBack}
            style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              color: '#374151'
            }}
            title={t('user_auth.buttons.back_to_login_title')}
          >
            {t('user_auth.buttons.back')}
          </button>
        )}
        <h1>{t('user_auth.title')}</h1>
        {!registerOnly && (
          <div className="auth-tabs">
            <button
              className={isLogin ? 'active' : ''}
              onClick={() => {
                setIsLogin(true);
                setError('');
                setSuccess('');
              }}
            >
              {t('user_auth.tabs.login')}
            </button>
            <button
              className={!isLogin ? 'active' : ''}
              onClick={() => {
                setIsLogin(false);
                setError('');
                setSuccess('');
              }}
            >
              {t('user_auth.tabs.register')}
            </button>
          </div>
        )}

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        {success && (
          <div className="auth-success">
            {success}
          </div>
        )}

        {isLogin && !registerOnly ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="login-email">{t('user_auth.login.email_label')}</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder={t('user_auth.login.email_placeholder')}
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">{t('user_auth.login.password_label')}</label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder={t('user_auth.login.password_placeholder')}
                autoComplete="current-password"
              />
            </div>
            <div style={{ marginBottom: '12px', textAlign: 'right' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: '0.88rem', padding: '6px 12px' }}
                onClick={() => {
                  setError('');
                  setShowForgotPassword(true);
                }}
              >
                {t('user_auth.login.forgot_password')}
              </button>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('user_auth.buttons.logging_in') : t('user_auth.tabs.login')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label htmlFor="register-email">{t('user_auth.register.email_label')}</label>
              <input
                id="register-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailCheck.status !== 'idle') {
                    setEmailCheck({ status: 'idle' });
                  }
                }}
                onBlur={(e) => runEmailCheck(e.target.value)}
                required
                disabled={loading}
                placeholder={t('user_auth.register.email_placeholder')}
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                aria-describedby="register-email-status"
              />
              <small style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                {t('user_auth.register.email_hint')}
              </small>
              <div
                id="register-email-status"
                aria-live="polite"
                style={{ fontSize: '0.85rem', marginTop: '4px', minHeight: '1.1em' }}
              >
                {emailCheck.status === 'checking' && (
                  <span style={{ color: '#6b7280' }}>
                    {t('user_auth.register.email_check_checking')}
                  </span>
                )}
                {emailCheck.status === 'ok' && (
                  <span style={{ color: '#16a34a' }}>
                    {t('user_auth.register.email_check_ok')}
                  </span>
                )}
                {emailCheck.status === 'invalid_format' && (
                  <span style={{ color: '#dc2626' }}>
                    {t('user_auth.register.email_check_invalid_format')}
                  </span>
                )}
                {emailCheck.status === 'unreachable_domain' && (
                  <span style={{ color: '#d97706' }}>
                    {t('user_auth.register.email_check_unreachable_domain')}
                  </span>
                )}
                {emailCheck.status === 'rate_limited' && (
                  <span style={{ color: '#dc2626' }}>
                    {t('user_auth.register.email_check_rate_limited')}
                  </span>
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="register-name">{t('user_auth.register.name_label')}</label>
              <input
                id="register-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
                placeholder={t('user_auth.register.name_placeholder')}
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-company">{t('user_auth.register.company_label')}</label>
              <input
                id="register-company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={loading}
                placeholder={t('user_auth.register.company_placeholder')}
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-address">{t('user_auth.register.address_label')}</label>
              <textarea
                id="register-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={loading}
                placeholder={t('user_auth.register.address_placeholder')}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-phone">{t('user_auth.register.phone_label')}</label>
              <input
                id="register-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
                placeholder={t('user_auth.register.phone_placeholder')}
                pattern="[\+]?[0-9\s\-\(\)]+"
              />
              <small style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                {t('user_auth.register.phone_hint')}
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="register-password">{t('user_auth.register.password_label')}</label>
              <input
                id="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder={t('user_auth.register.password_placeholder')}
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-confirm-password">{t('user_auth.register.confirm_password_label')}</label>
              <input
                id="register-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder={t('user_auth.register.confirm_password_placeholder')}
                minLength={8}
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <input
                id="register-accept-terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={loading}
                style={{ marginTop: '4px' }}
                required
              />
              <label htmlFor="register-accept-terms" style={{ fontSize: '0.85rem', lineHeight: '1.35' }}>
                <Trans
                  i18nKey="user_auth.register.terms_label"
                  values={{ version: TERMS_VERSION }}
                  components={{
                    1: (
                      <a
                        href={currentTermsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb', textDecoration: 'underline' }}
                      >
                        {t('user_auth.register.terms_link_text')}
                      </a>
                    ),
                  }}
                />
              </label>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('user_auth.buttons.registering') : t('user_auth.tabs.register')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default UserAuth;


